import React from 'react';
import { useTranslation } from 'react-i18next';
import { useChartConfig } from './hooks/useChartConfig';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export interface QuestionnaireSubmission {
  userId: string;
  userName: string;
  userEmail: string;
  questionnaireId: string;
  totalQuestions: number;
  answeredQuestions: number;
  completionPercentage: number;
  submittedAt: string;
}

interface Props {
  data: QuestionnaireSubmission[];
}

// Helper to group submissions by date (YYYY-MM-DD)
function groupByDate(data: QuestionnaireSubmission[]) {
  const counts: Record<string, number> = {};
  data.forEach((item) => {
    const date = item.submittedAt.slice(0, 10); // YYYY-MM-DD
    counts[date] = (counts[date] || 0) + 1;
  });
  // Convert to array sorted by date
  return Object.entries(counts)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export const QuestionnaireSubmissionTrendChart: React.FC<Props> = ({
  data,
}) => {
  const { t } = useTranslation();
  const chartData = groupByDate(data);
  const config = useChartConfig();

  return (
    <>
      <style>
        {`
          .resp-container, .resp-container * {
            outline: none !important;
          }
        `}
      </style>
      <ResponsiveContainer
        width="100%"
        height={410}
        style={config.styles.noOutline}
        className="resp-container"
      >
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 0, bottom: 50 }}
          onClick={undefined}
          style={config.styles.noOutline}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={config.colors.grid}
            onClick={undefined}
            style={config.styles.noOutline}
          />
          <XAxis
            style={config.styles.noOutline}
            dataKey="date"
            {...config.axis}
            label={{
              value: t('dashboard.dataLabels.date'),
              position: 'bottom',
              offset: 20,
            }}
          />
          <YAxis
            style={config.styles.noOutline}
            allowDecimals={false}
            label={{
              value: t('dashboard.dataLabels.submissions'),
              angle: -90,
              position: 'insideLeft',
            }}
            {...config.axis}
          />
          <Tooltip {...config.tooltip} />
          <Line
            type="natural"
            dataKey="count"
            stroke={config.colors.primary}
            strokeWidth={3}
            dot={false}
            style={config.styles.noOutline}
          />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};
